import React, { useId } from "react";
import {
  autoUpdate,
  FloatingPortal,
  shift,
  useFloating,
  type Placement,
} from "@floating-ui/react";
import { Popover } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { twJoin, twMerge } from "tailwind-merge";
import { Calendar20Icon, ChevronIcon } from "@components/icons";
import { Label } from "@components/Label/Label";
import { DueDayPickerContent } from "./components/DueDayPickerContent";

interface IDueDayPickerProps {
  value?: number;
  onChange: (value: number) => void;
  placement?: Placement;
  isPositionFixed?: boolean;
  showWholeDate?: boolean;
}

export const DueDayPicker: React.FC<IDueDayPickerProps> = (props) => {
  const { value, onChange, placement, isPositionFixed } = props;
  const { t } = useTranslation();
  const id = useId();

  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: placement ?? "bottom-end",
    strategy: isPositionFixed ? "fixed" : "absolute",
    middleware: [
      shift({
        padding: { bottom: 16 },
        crossAxis: true,
      }),
    ],
  });

  return (
    <div className="flex w-full flex-col gap-1.5">
      <Label htmlFor={id}>{t("dueDayPicker.label")}</Label>
      <Popover className="relative">
        {({ open, close }) => (
          <React.Fragment>
            <Popover.Button
              id={id}
              ref={refs.setReference}
              className={twMerge(
                "flex h-11 w-full items-center rounded-full border border-grey-hover bg-base-white transition-shadow focus-within:border-green-dark focus-within:ring-4 focus-within:ring-green-dark focus-within:ring-opacity-10",
                open && "ring-4 ring-base-light-grey ring-opacity-100",
              )}
            >
              <Calendar20Icon className="ml-3.5 size-5 text-grey-dark" />
              <span
                className={twMerge(
                  "flex-grow px-1.5 text-left",
                  !value && "text-grey-dark",
                )}
              >
                {value ?? t("dueDayPicker.placeholder")}
              </span>
              <ChevronIcon
                className={twJoin(
                  "mr-3.5 size-5 transition-transform",
                  open && "rotate-180",
                )}
              />
            </Popover.Button>
            <FloatingPortal>
              <Popover.Panel
                ref={refs.setFloating}
                style={floatingStyles}
                className="absolute z-[9999] mt-2 rounded-xl border border-grey-light bg-white shadow-lg"
              >
                <DueDayPickerContent
                  value={value}
                  close={close}
                  onChange={onChange}
                />
              </Popover.Panel>
            </FloatingPortal>
          </React.Fragment>
        )}
      </Popover>
    </div>
  );
};
